import React, { createContext, useContext, useState, useCallback } from "react";
import {
  Register,
  adjustRegister as adjustRegisterUtil,
  noteToMidi,
  midiToNote,
} from "../utils/registerVoicing";

// Define the context interface
interface RegisterContextType {
  register: Register | null;
  setRegister: (register: Register | null) => void;
  adjustRegister: (semitones: number) => void;
  setBottomNote: (note: string) => void;
  setTopNote: (note: string) => void;
}

// Create the context with default values
const RegisterContext = createContext<RegisterContextType>({
  register: null,
  setRegister: () => {},
  adjustRegister: () => {},
  setBottomNote: () => {},
  setTopNote: () => {},
});

// Custom hook for accessing the register context
export const useRegister = () => useContext(RegisterContext);

// Provider component
export const RegisterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State for register
  const [register, setRegister] = useState<Register | null>(null);

  // Adjust register by semitones
  const adjustRegister = useCallback(
    (semitones: number) => {
      if (register) {
        setRegister(adjustRegisterUtil(register, semitones));
      }
    },
    [register]
  );

  // Set bottom note directly (maintains minimum 12 semitones height)
  const setBottomNote = useCallback(
    (note: string) => {
      if (register) {
        const newBottomMidi = noteToMidi(note);
        const currentTopMidi = noteToMidi(register.topNote);

        // Ensure minimum 12 semitones height
        const minTopMidi = newBottomMidi + 12;
        const newTopMidi = Math.max(currentTopMidi, minTopMidi);

        // Ensure we don't exceed C8 (MIDI 108)
        const maxTopMidi = Math.min(newTopMidi, 108);

        setRegister({
          bottomNote: note,
          topNote: midiToNote(maxTopMidi),
        });
      }
    },
    [register]
  );

  // Set top note directly (maintains minimum 12 semitones height)
  const setTopNote = useCallback(
    (note: string) => {
      if (register) {
        const newTopMidi = noteToMidi(note);
        const currentBottomMidi = noteToMidi(register.bottomNote);

        // Ensure minimum 12 semitones height
        const maxBottomMidi = newTopMidi - 12;
        const newBottomMidi = Math.min(currentBottomMidi, maxBottomMidi);

        // Ensure we don't go below C0 (MIDI 12)
        const minBottomMidi = Math.max(newBottomMidi, 12);

        setRegister({
          bottomNote: midiToNote(minBottomMidi),
          topNote: note,
        });
      }
    },
    [register]
  );

  // Value object for the context
  const contextValue: RegisterContextType = {
    register,
    setRegister,
    adjustRegister,
    setBottomNote,
    setTopNote,
  };

  return (
    <RegisterContext.Provider value={contextValue}>
      {children}
    </RegisterContext.Provider>
  );
};
